/**
 * Custom pointer-based drag for dragging a panel/menu tile onto the poster canvas, or onto a registered drop
 * target such as a folder card.
 *
 * Native HTML5 drag-and-drop can't give the UX we want (it paints its own ghost with the tile background,
 * shows a "copy" + badge, and forces the OS arrow cursor — CSS cursors are ignored mid-drag). So this drives
 * the drag with pointer events: a floating clone of the thumbnail follows the cursor, the body cursor is a
 * closed hand the whole time, and on release the whiteboard resolves whether the drop landed on the canvas.
 *
 * The drop target (whiteboard) registers a resolver here; the drag source (a grid/carousel tile, a text menu
 * tile, …) starts a drag. Mirrors the `stock-media-popup-controller` singleton pattern. Other drop targets
 * (folder cards in the uploads modal) register their element under an id; the hovered one is re-resolved on
 * every pointer move so it can highlight, and the drag source receives that id on release.
 */

import type {DragEvent as ReactDragEvent, PointerEvent as ReactPointerEvent} from 'react';
import type {ItemCoordinates, ItemData} from '@PosterWhiteboard/page/add-item.class';

export type CanvasDropPosition = ItemCoordinates;

type CanvasDropHandler = (point: CanvasDropPosition) => void;
export type CanvasDropResolver = (e: PointerEvent) => CanvasDropPosition | null;

export interface DropTargetRegistration {
  element: HTMLElement;
  onHoverChange: (isHovered: boolean) => void;
}

export interface DragGhostBadge {
  count: number;
  /** Styles the badge; the library owns no CSS, so the drag source supplies a class from its own module. */
  className: string;
}

/** Several tiles in hand shown as a fanned stack of up to three bare, tile-sized previews (thumbs, or whole cards when `cloneFullElement` is set) instead of the full-size clone. A single tile keeps the clone. */
export interface DragGhostStack {
  /** The dragged tiles' elements, the pressed one first. Only the first `GHOST_STACK_PREVIEW_LIMIT` are shown. */
  sourceElements: HTMLElement[];
}

export interface BeginTileDragOptions {
  event: ReactPointerEvent<HTMLElement>;
  /** Receives the poster-space point when the drag is released over the canvas (see `registerCanvasDropResolver`). */
  onDropOnCanvas?: CanvasDropHandler;
  /** Receives the id of the registered drop target (see `registerDropTarget`) the drag was released over. */
  onDropOnTarget?: (targetId: string) => void;
  /** Count badge pinned to the ghost's corner when several items travel together. */
  ghostBadge?: DragGhostBadge;
  /** Show several dragged tiles as the My Stuff-style preview fan instead of the full-size clone (see `DragGhostStack`). */
  ghostStack?: DragGhostStack;
  /** Scrollable ancestor to nudge while the pointer sits near its top/bottom edge, so off-screen drop targets stay reachable. */
  scrollContainer?: HTMLElement | null;
  /**
   * Clone the whole tile as the drag ghost instead of just its inner `<img>`. Media tiles (image/video/sticker)
   * are essentially the image, so the bare-img ghost is right for them; audio tiles are rich cards (thumbnail +
   * title + duration + waveform), so they need the full element to look the same while dragging. When the source
   * is an `<li>`, the full-element clone also strips the list-marker bullet and the tile's own square background
   * so only the card shows — a no-op for non-list tiles, which keep their own background/radius.
   */
  cloneFullElement?: boolean;
}

interface TileDragPropsOptions extends Pick<BeginTileDragOptions, 'cloneFullElement'> {
  enableDragToCanvas: boolean;
  onDropOnCanvas: CanvasDropHandler;
}

/** The drag-related DOM props a tile spreads onto its `ClickableDiv`. Empty when the tile isn't drag-enabled. */
interface TileDragProps {
  onPointerDown?: (event: ReactPointerEvent<HTMLElement>) => void;
  onDragStart?: (event: ReactDragEvent) => void;
}

const DRAG_START_THRESHOLD = 5;
const CLONE_OPACITY = '0.85';
const CLONE_Z_INDEX = '2147483646';
/** Matches the audio card's `radius-12` corner so the full-element ghost is clipped to the same rounded shape. */
const CLONE_FULL_CORNER_RADIUS = '12px';
const GRABBING_CURSOR_STYLE_ID = 'pmw-canvas-item-drag-cursor';
const DRAG_CANCEL_KEY = 'Escape';
/** Pointer within this many px of the scroll container's top/bottom edge keeps it scrolling. */
const AUTO_SCROLL_EDGE_SIZE = 48;
const AUTO_SCROLL_STEP = 12;
export const GHOST_STACK_PREVIEW_LIMIT = 3;
/** On `<body>` for the whole drag, so stylesheets can quiet non-target affordances (e.g. the folders grid's create tile). */
export const TILE_DRAG_ACTIVE_BODY_CLASS = 'tile-drag-active';
/** Marks tile chrome that must not appear in a drag preview (selection overlays, counters). */
export const DRAG_GHOST_EXCLUDE_ATTRIBUTE = 'data-drag-ghost-exclude';
/** The stack hangs off the cursor's lower-right instead of keeping the grab point, like My Stuff. */
const GHOST_STACK_POINTER_OFFSET = 18;

let dropResolver: CanvasDropResolver | null = null;
const dropTargets = new Map<string, DropTargetRegistration>();

/**
 * Global drag markers for the duration of the drag: the grabbing cursor everywhere (a global `!important` rule beats
 * per-element cursors — canvas, buttons, etc.) and the body class stylesheets key off.
 */
const markDragActiveGlobally = (): void => {
  document.body.classList.add(TILE_DRAG_ACTIVE_BODY_CLASS);
  if (document.getElementById(GRABBING_CURSOR_STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = GRABBING_CURSOR_STYLE_ID;
  style.textContent = '* { cursor: grabbing !important; }';
  document.head.appendChild(style);
};

const clearGlobalDragMarkers = (): void => {
  document.body.classList.remove(TILE_DRAG_ACTIVE_BODY_CLASS);
  document.getElementById(GRABBING_CURSOR_STYLE_ID)?.remove();
};

/** Anchors an item at the point it was dropped. An item that was clicked rather than dragged keeps the page's default placement. */
export function placeItemAtDropPosition<T extends ItemData>(item: T, dropPosition?: CanvasDropPosition): T {
  if (!dropPosition) {
    return item;
  }
  return {...item, left: dropPosition.left, top: dropPosition.top};
}

/** Called by the whiteboard. Returns the poster-space point for a release over the canvas, else null. */
export function registerCanvasDropResolver(resolver: CanvasDropResolver): () => void {
  dropResolver = resolver;
  return (): void => {
    if (dropResolver === resolver) {
      dropResolver = null;
    }
  };
}

/** Called by a drop target (e.g. a folder card). The id comes back to the drag source's `onDropOnTarget` on release. */
export function registerDropTarget(targetId: string, registration: DropTargetRegistration): () => void {
  dropTargets.set(targetId, registration);
  return (): void => {
    if (dropTargets.get(targetId) === registration) {
      dropTargets.delete(targetId);
    }
  };
}

/** The ghost is `pointer-events: none`, so `elementFromPoint` sees straight through it to whatever is underneath. */
const findDropTargetIdAt = (x: number, y: number): string | null => {
  if (!dropTargets.size) {
    return null;
  }
  const elementUnderPointer = document.elementFromPoint(x, y);
  if (!elementUnderPointer) {
    return null;
  }
  for (const [targetId, {element}] of dropTargets) {
    if (element.contains(elementUnderPointer)) {
      return targetId;
    }
  }
  return null;
};

const getAutoScrollDirection = (scrollContainer: HTMLElement | null | undefined, pointerY: number): -1 | 0 | 1 => {
  if (!scrollContainer) {
    return 0;
  }
  const bounds = scrollContainer.getBoundingClientRect();
  if (pointerY < bounds.top + AUTO_SCROLL_EDGE_SIZE) {
    return -1;
  }
  if (pointerY > bounds.bottom - AUTO_SCROLL_EDGE_SIZE) {
    return 1;
  }
  return 0;
};

/**
 * A tile cloned out of its `<ul>` regains the list-marker bullet and carries its own square tile background. Strip
 * the marker, drop the tile background, and clip to the card's rounded corners so only the rounded card shows.
 * Non-list tiles (e.g. text tiles, `<div role="button">` with their own gray-0 background + radius-8) are untouched.
 */
const stripListTileChrome = (clone: HTMLElement, source: HTMLElement): void => {
  if (source.tagName !== 'LI') {
    return;
  }
  clone.style.background = 'transparent';
  clone.style.borderRadius = CLONE_FULL_CORNER_RADIUS;
  clone.style.listStyle = 'none';
  clone.style.overflow = 'hidden';
};

const removeGhostExcludedElements = (clone: HTMLElement): void => {
  clone.querySelectorAll(`[${DRAG_GHOST_EXCLUDE_ATTRIBUTE}]`).forEach((excludedEl) => {
    excludedEl.remove();
  });
};

/** data-canvas-drag-thumb marks the tile's designated thumb element (e.g. the shape tile's recolored <img>). */
const getDragThumb = (sourceEl: HTMLElement): HTMLElement => {
  return sourceEl.querySelector<HTMLElement>('img, [data-canvas-drag-thumb]') ?? sourceEl;
};

/**
 * A bare copy of `source` for a drag preview. The ghost is repositioned on every pointermove, so any inherited
 * transition (tiles carry `hover-transition-all`, which animates `all`) would ease each step and leave it trailing the cursor.
 */
const cloneForDragGhost = (source: HTMLElement): HTMLElement => {
  const clone = source.cloneNode(true) as HTMLElement;
  clone.removeAttribute('draggable');
  removeGhostExcludedElements(clone);
  clone.style.animation = 'none';
  clone.style.margin = '0';
  clone.style.transition = 'none';
  return clone;
};

const createDragClone = (sourceEl: HTMLElement, rect: DOMRect, cloneFullElement: boolean): HTMLElement => {
  const source = cloneFullElement ? sourceEl : getDragThumb(sourceEl);
  const clone = cloneForDragGhost(source);
  clone.style.height = `${rect.height}px`;
  clone.style.left = `${rect.left}px`;
  clone.style.opacity = CLONE_OPACITY;
  clone.style.pointerEvents = 'none';
  clone.style.position = 'fixed';
  clone.style.top = `${rect.top}px`;
  clone.style.width = `${rect.width}px`;
  clone.style.zIndex = CLONE_Z_INDEX;
  if (!cloneFullElement) {
    clone.style.objectFit = 'contain';
    return clone;
  }
  stripListTileChrome(clone, source);
  return clone;
};

interface CreateDragGhostParams {
  sourceEl: HTMLElement;
  rect: DOMRect;
  cloneFullElement: boolean;
  badge?: DragGhostBadge;
  stack?: DragGhostStack;
}

interface CreateBadgedDragGhostParams extends Omit<CreateDragGhostParams, 'badge' | 'stack'> {
  badge: DragGhostBadge;
}

interface GhostStackFrameLayout {
  left: number;
  top: number;
  rotateDegrees: number;
}

/** How the fan is laid out. Each frame is the size its tile has on screen, so the fanned previews are as big as a single-tile clone. */
interface GhostStackGeometry {
  transformOrigin: string;
  /** Back-to-front frame placement; the last entry is the frame on top. */
  frameLayouts: readonly GhostStackFrameLayout[];
}

/** Thumb tiles (image/video): My Stuff's poster-card fan. */
const THUMB_GHOST_STACK_GEOMETRY: GhostStackGeometry = {
  transformOrigin: 'center bottom',
  frameLayouts: [
    {left: -13, top: -11, rotateDegrees: -14},
    {left: -7, top: -5, rotateDegrees: -6},
    {left: 0, top: 0, rotateDegrees: 3},
  ],
};

/** Whole cards (audio tiles): My Stuff's wide folder-row fan. */
const FULL_TILE_GHOST_STACK_GEOMETRY: GhostStackGeometry = {
  transformOrigin: 'left center',
  frameLayouts: [
    {left: -5, top: -8, rotateDegrees: -9},
    {left: -3, top: -4, rotateDegrees: -3},
    {left: 0, top: 0, rotateDegrees: 1},
  ],
};

/** One tile — or a stack whose other tiles are all scrolled out of the DOM — drags as the full-size clone, like the stock panel. */
const hasSeveralStackedTiles = (stack: DragGhostStack | undefined): stack is DragGhostStack => {
  return (stack?.sourceElements.length ?? 0) > 1;
};

const createFixedGhostBox = (width: number, height: number): HTMLElement => {
  const ghost = document.createElement('div');
  ghost.style.height = `${height}px`;
  ghost.style.pointerEvents = 'none';
  ghost.style.position = 'fixed';
  ghost.style.width = `${width}px`;
  ghost.style.zIndex = CLONE_Z_INDEX;
  return ghost;
};

/** The count badge only shows while several items travel together. */
const hasCountBadge = (badge: DragGhostBadge | undefined): badge is DragGhostBadge => {
  return (badge?.count ?? 0) > 1;
};

const appendGhostBadge = (ghost: HTMLElement, badge: DragGhostBadge | undefined): void => {
  if (!hasCountBadge(badge)) {
    return;
  }
  const badgeEl = document.createElement('span');
  badgeEl.className = badge.className;
  badgeEl.textContent = `${badge.count}`;
  ghost.appendChild(badgeEl);
};

/** The tile's thumb (`<img>` or marked element), stretched to fill the frame and letterboxed. */
const createThumbPreview = (sourceEl: HTMLElement): HTMLElement => {
  const preview = cloneForDragGhost(getDragThumb(sourceEl));
  preview.style.display = 'block';
  preview.style.height = '100%';
  preview.style.objectFit = 'contain';
  preview.style.width = '100%';
  return preview;
};

/** The whole tile filling its tile-sized frame, list chrome stripped. */
const createFullTilePreview = (sourceEl: HTMLElement): HTMLElement => {
  const preview = cloneForDragGhost(sourceEl);
  preview.style.height = '100%';
  preview.style.width = '100%';
  stripListTileChrome(preview, sourceEl);
  return preview;
};

/** The tile's bare preview, sized to fill whatever box holds it. */
const createTilePreview = (sourceEl: HTMLElement, cloneFullElement: boolean): HTMLElement => {
  return cloneFullElement ? createFullTilePreview(sourceEl) : createThumbPreview(sourceEl);
};

interface CreateGhostStackFrameParams {
  sourceEl: HTMLElement;
  layout: GhostStackFrameLayout;
  geometry: GhostStackGeometry;
  cloneFullElement: boolean;
}

/** One frame of the fan: the tile's bare preview at its on-screen size — like the stock panel's clone — at its slot's offset and tilt. */
const createGhostStackFrame = ({sourceEl, layout, geometry, cloneFullElement}: CreateGhostStackFrameParams): HTMLElement => {
  const tileRect = sourceEl.getBoundingClientRect();
  const frame = document.createElement('div');
  frame.style.height = `${tileRect.height}px`;
  frame.style.left = `${layout.left}px`;
  frame.style.overflow = 'hidden';
  frame.style.position = 'absolute';
  frame.style.top = `${layout.top}px`;
  frame.style.transform = `rotate(${layout.rotateDegrees}deg)`;
  frame.style.transformOrigin = geometry.transformOrigin;
  frame.style.width = `${tileRect.width}px`;
  frame.appendChild(createTilePreview(sourceEl, cloneFullElement));
  return frame;
};

/**
 * The frames share the clone's translucency as one group: per-frame opacity would let the back frames show through
 * the pressed one, and keeping the badge outside the group leaves it opaque, as on the clone.
 */
const createGhostStackFramesLayer = (frames: HTMLElement[]): HTMLElement => {
  const layer = document.createElement('div');
  layer.style.inset = '0';
  layer.style.opacity = CLONE_OPACITY;
  layer.style.position = 'absolute';
  layer.append(...frames);
  return layer;
};

/**
 * The preview stack: up to three previews fanned at slight angles with the pressed tile on top, the way My Stuff
 * drags designs into folders. `sourceElements` arrives pressed-first, so the shown slice is reversed to paint the
 * pressed tile last. The fixed box is the size of the top frame, so the count badge hugs it.
 */
const createStackedDragGhost = ({sourceElements}: DragGhostStack, badge: DragGhostBadge | undefined, cloneFullElement: boolean): HTMLElement => {
  const geometry = cloneFullElement ? FULL_TILE_GHOST_STACK_GEOMETRY : THUMB_GHOST_STACK_GEOMETRY;
  const sourcesBackToFront = sourceElements.slice(0, GHOST_STACK_PREVIEW_LIMIT).reverse();
  const frames = sourcesBackToFront.map((sourceEl, frameIndex) => {
    return createGhostStackFrame({sourceEl, layout: geometry.frameLayouts[frameIndex], geometry, cloneFullElement});
  });
  const topFrame = frames[frames.length - 1];
  const ghost = createFixedGhostBox(parseFloat(topFrame.style.width), parseFloat(topFrame.style.height));
  ghost.appendChild(createGhostStackFramesLayer(frames));
  appendGhostBadge(ghost, badge);
  return ghost;
};

/**
 * The element that follows the cursor: the preview stack when several stacked tiles travel, else the bare clone, or
 * a box carrying the tile's preview plus a count badge. The box exists because the clone may be a bare `<img>`, which
 * cannot hold a child.
 */
const createDragGhost = ({sourceEl, rect, cloneFullElement, badge, stack}: CreateDragGhostParams): HTMLElement => {
  if (hasSeveralStackedTiles(stack)) {
    return createStackedDragGhost(stack, badge, cloneFullElement);
  }
  if (!hasCountBadge(badge)) {
    return createDragClone(sourceEl, rect, cloneFullElement);
  }
  return createBadgedDragGhost({sourceEl, rect, cloneFullElement, badge});
};

/** One tile's preview at its on-screen spot, translucent like the clone, with the count badge left opaque beside it. */
const createBadgedDragGhost = ({sourceEl, rect, cloneFullElement, badge}: CreateBadgedDragGhostParams): HTMLElement => {
  const ghost = createFixedGhostBox(rect.width, rect.height);
  ghost.style.left = `${rect.left}px`;
  ghost.style.top = `${rect.top}px`;
  const preview = createTilePreview(sourceEl, cloneFullElement);
  preview.style.opacity = CLONE_OPACITY;
  ghost.appendChild(preview);
  appendGhostBadge(ghost, badge);
  return ghost;
};

/**
 * Swallows the `click` the browser fires after a drag's `pointerup`. Installed only once the drop handler has
 * run, because a handler may dispatch its own click to dismiss the menu it was dragged out of — that one has to
 * get through, and the browser's trailing click is the one that must not reach the canvas.
 */
const suppressNextClick = (): void => {
  const block = (ev: MouseEvent): void => {
    ev.preventDefault();
    ev.stopPropagation();
  };
  document.addEventListener('click', block, {capture: true, once: true});
  window.setTimeout(() => {
    document.removeEventListener('click', block, true);
  }, 0);
};

/** A drag cancelled while the button is still down must not turn into a click on its tile, which would add the item. */
const suppressClickAfterRelease = (): void => {
  document.addEventListener('pointerup', suppressNextClick, {capture: true, once: true});
};

/** The key that cancels a live drag is the drag's alone: a modal around the tiles (react-modal) must not close on it too. */
const claimDragCancelKey = (e: KeyboardEvent): void => {
  e.preventDefault();
  e.stopPropagation();
};

/**
 * The `onPointerDown` entry point every draggable tile uses. It only starts *tracking* a potential drag: the drag
 * itself "begins" (clone + grabbing cursor) once the pointer moves past a small threshold, so a plain click still
 * adds the item the normal way. Right- and middle-button presses fall through untouched so they still reach their
 * own menus.
 */
export function beginTileDrag({event, onDropOnCanvas, onDropOnTarget, cloneFullElement = false, ghostBadge, ghostStack, scrollContainer}: BeginTileDragOptions): void {
  if (event.button !== 0) {
    return;
  }
  const sourceEl = event.currentTarget;
  const startClientX = event.clientX;
  const startClientY = event.clientY;
  const rect = sourceEl.getBoundingClientRect();
  const isStackedGhost = hasSeveralStackedTiles(ghostStack);
  // The full-size clone keeps the grab point under the cursor; the preview stack hangs off the cursor's lower-right.
  const ghostOffsetX = isStackedGhost ? -GHOST_STACK_POINTER_OFFSET : startClientX - rect.left;
  const ghostOffsetY = isStackedGhost ? -GHOST_STACK_POINTER_OFFSET : startClientY - rect.top;
  let started = false;
  let ghost: HTMLElement | null = null;
  let hoveredTargetId: string | null = null;
  let lastPointerX = startClientX;
  let lastPointerY = startClientY;
  let autoScrollFrame: number | null = null;

  const positionGhost = (x: number, y: number): void => {
    if (ghost) {
      ghost.style.left = `${x - ghostOffsetX}px`;
      ghost.style.top = `${y - ghostOffsetY}px`;
    }
  };

  const setHoveredTarget = (targetId: string | null): void => {
    if (targetId === hoveredTargetId) {
      return;
    }
    if (hoveredTargetId) {
      dropTargets.get(hoveredTargetId)?.onHoverChange(false);
    }
    hoveredTargetId = targetId;
    if (targetId) {
      dropTargets.get(targetId)?.onHoverChange(true);
    }
  };

  const stopAutoScroll = (): void => {
    if (autoScrollFrame !== null) {
      cancelAnimationFrame(autoScrollFrame);
      autoScrollFrame = null;
    }
  };

  const autoScrollTick = (): void => {
    autoScrollFrame = null;
    const direction = getAutoScrollDirection(scrollContainer, lastPointerY);
    if (!scrollContainer || direction === 0) {
      return;
    }
    scrollContainer.scrollTop += direction * AUTO_SCROLL_STEP;
    // Targets slide under a stationary pointer while scrolling, so the hovered one must be re-resolved.
    setHoveredTarget(findDropTargetIdAt(lastPointerX, lastPointerY));
    autoScrollFrame = requestAnimationFrame(autoScrollTick);
  };

  const scheduleAutoScroll = (): void => {
    autoScrollFrame ??= requestAnimationFrame(autoScrollTick);
  };

  const cleanup = (): void => {
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
    document.removeEventListener('pointercancel', cleanup);
    document.removeEventListener('keydown', onKeyDown, {capture: true});
    stopAutoScroll();
    setHoveredTarget(null);
    if (ghost) {
      ghost.remove();
      ghost = null;
    }
    clearGlobalDragMarkers();
    document.body.style.userSelect = '';
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key !== DRAG_CANCEL_KEY) {
      return;
    }
    if (started) {
      claimDragCancelKey(e);
      suppressClickAfterRelease();
    }
    cleanup();
  };

  const onMove = (e: PointerEvent): void => {
    lastPointerX = e.clientX;
    lastPointerY = e.clientY;
    if (!started) {
      if (Math.abs(e.clientX - startClientX) < DRAG_START_THRESHOLD && Math.abs(e.clientY - startClientY) < DRAG_START_THRESHOLD) {
        return;
      }
      started = true;
      ghost = createDragGhost({sourceEl, rect, cloneFullElement, badge: ghostBadge, stack: ghostStack});
      document.body.appendChild(ghost);
      markDragActiveGlobally();
      document.body.style.userSelect = 'none';
    }
    positionGhost(e.clientX, e.clientY);
    setHoveredTarget(findDropTargetIdAt(e.clientX, e.clientY));
    scheduleAutoScroll();
  };

  const onUp = (e: PointerEvent): void => {
    const wasDragging = started;
    const droppedTargetId = hoveredTargetId;
    cleanup();
    if (!wasDragging) {
      return;
    }
    try {
      if (droppedTargetId && onDropOnTarget) {
        onDropOnTarget(droppedTargetId);
      } else if (onDropOnCanvas) {
        const point = dropResolver ? dropResolver(e) : null;
        if (point) {
          onDropOnCanvas(point);
        }
      }
    } finally {
      suppressNextClick();
    }
  };

  document.addEventListener('pointermove', onMove);
  document.addEventListener('pointerup', onUp);
  document.addEventListener('pointercancel', cleanup);
  // Capture phase: react-modal's content stops Escape from bubbling, so a bubble listener here never sees it.
  document.addEventListener('keydown', onKeyDown, {capture: true});
}

/**
 * Builds the `onPointerDown` handler for a grid tile that adds `item` to the poster, positioned at the drop point
 * when dragged. `shouldCloneFullElement` mirrors `BeginTileDragOptions.cloneFullElement`, decided per item (e.g.
 * audio tiles need the full-card ghost).
 */
export function createGridItemDragHandler<TItem>(
  onDropItem: (item: TItem, dropPosition: CanvasDropPosition) => unknown,
  shouldCloneFullElement?: (item: TItem) => boolean
): (item: TItem, event: ReactPointerEvent<HTMLElement>) => void {
  return (item, event) => {
    beginTileDrag({
      event,
      onDropOnCanvas: (dropPosition) => void onDropItem(item, dropPosition),
      cloneFullElement: shouldCloneFullElement?.(item),
    });
  };
}

/**
 * Builds the drag wiring for a standalone tile (a text preset, a text option, a "do more with text" tile): a
 * left-press starts the canvas drag, and the browser's own image drag is blocked so the two can't run at once.
 */
export function createTileDragProps({enableDragToCanvas, onDropOnCanvas, cloneFullElement}: TileDragPropsOptions): TileDragProps {
  if (!enableDragToCanvas) {
    return {};
  }
  return {
    onPointerDown: (event): void => {
      beginTileDrag({event, onDropOnCanvas, cloneFullElement});
    },
    onDragStart: (event): void => {
      event.preventDefault();
    },
  };
}
